import { Component, Inject, LOCALE_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerService } from '../services/customer.service';
import { Customer } from '../../shared/models/customer.interface';
import { CustomerNote } from '../../shared/models/customer.note.interface';
import { Quote } from '../../shared/models/quote.interface';
import { MatRadioChange } from '@angular/material/radio';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Contact } from '../../shared/models/contact.interface';
import { ContactType } from '../../shared/models/contact.type.interface';
import { FileService } from '../../shared/services/file.service';
import { finalize } from 'rxjs/operators';
import { FileUpload } from '../../shared/models/file.upload.interface';
import { RolePermission } from '../../shared/models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { NgForm, ValidationErrors } from '@angular/forms';
import { MessageNnotificationTypes_Enum } from '../../admin/enums/message-notification-types.enum';


export interface ContactDialogData {
  contact: Contact;
  contactTypes: ContactType[];
  notificationTypes: [];
}
export interface NoteDialogData {
  note: CustomerNote;
}
export interface DocumentDialogData {
  document: CustomerNote;

  noteFile: any;
  messages: string[];
}

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent extends AbstractBiditComponent {
  errors: string;
  isRequesting: boolean;

  customer: Customer;
  jobs: Quote[];
  notes: CustomerNote[];
  documents: CustomerNote[];
  primaryContact: string;
  contactTypes: ContactType[];

  customerTabIndex = 0;
  canEdit = true;
  canAddQuote = true;

  constructor(@Inject(LOCALE_ID) private locale: string,
    private dialog: MatDialog, deviceDetectorService: DeviceDetectorService,
    private fileService: FileService, private customerService: CustomerService,
    private route: ActivatedRoute, private router: Router) {
    super(deviceDetectorService);
  }

  init() {
    this.canEdit = CustomerService.hasPermission(RolePermission.EditCustomer);
    this.canAddQuote = CustomerService.hasPermission(RolePermission.AddQuote);

    this.route.paramMap.subscribe(params => {

      const customerId = params.get('customerId');
      this.loadCustomer(parseInt(customerId));
    });

    this.customerService.getContactTypes()
      .subscribe({
        next: (data) => {
          this.contactTypes = data;
        },
        error: (error: string) => this.onError(error)
      });
  }

  get noteLabel(): string {
    if (this.locale == 'es')
      return `Notas (${this.notes?.length})`;

    return `Notes (${this.notes?.length})`;
  }

  get documentLabel(): string {
    if (this.locale == 'es')
      return `Documentos (${this.documents?.length})`;

    return `Documents (${this.documents?.length})`;
  }

  customerUpdated(customer: Customer) {
    this.loadCustomer(customer.customer_ID);
  }

  loadCustomer(customerId: number) {
    this.customerService.getCustomer(customerId)
      .subscribe({
        next: (data) => {
          this.customer = data;

          this.primaryContact = (data.mainContact == null ? null : data.mainContact.contact_ID.toString())

          this.jobs = data.quotes.filter(q => q.quote_Converted);
          this.notes = data.customerNotes.filter(n => n.note_Attachment == null);
          this.documents = data.customerNotes.filter(n => n.note_Attachment != null);
        },
        error: (error: string) => this.onError(error)
      });
  }

  deleteNote(note: CustomerNote) {
    const proceed = confirm($localize`Delete selected item?`);
    if (!proceed) return;

    this.customerService.deleteNote(note.note_ID)
      .subscribe({
        next: (data) => {
          this.loadCustomer(this.customer.customer_ID);
        },
        error: (error: string) => this.onError(error)
      });

  }

  editNote(note: CustomerNote) {
    // clone object to keep changes local
    const editNote = (note != null) ?
      (<any>Object).assign({}, note) as CustomerNote :
      {
        customer_ID: this.customer.customer_ID
      } as CustomerNote;

    const dialogRef = this.dialog.open(CustomerNoteDialog, {
      width: '1250px',
      data: {
        note: editNote
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.loadCustomer(this.customer.customer_ID);
        }
      }
    });
  }

  editDocument(document: CustomerNote) {
    // clone object to keep changes local
    const editDocument = (document != null) ?
      (<any>Object).assign({}, document) as CustomerNote :
      {
        customer_ID: this.customer.customer_ID
      } as CustomerNote;

    const dialogRef = this.dialog.open(CustomerDocumentDialog, {
      width: '1250px',
      data: {
        document: editDocument
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.loadCustomer(this.customer.customer_ID);
        }
      }
    });
  }

  toggleContactStatus(contact: Contact, $event): void {
    contact.contact_IsActive = !contact.contact_IsActive;

    // cannot deactivate the primary contact
    const setPrimaryInactive = this.customer.mainContact.contact_ID == contact.contact_ID && !contact.contact_IsActive;
    if (setPrimaryInactive) {
      $event.target.checked = false;
      contact.contact_IsActive = true;
      alert($localize`Cannot deactivate the primary contact`);
      return;
    }

    // must have at least one active contact for customer
    const activeContact = this.customer.contacts.find(c => c.contact_IsActive);
    if (activeContact == null) {
      $event.target.checked = false;
      contact.contact_IsActive = true;
      alert($localize`One active contact required`);
      return;
    }

    this.isRequesting = true;
    this.customerService.setContactStatus(contact.contact_ID, contact.contact_IsActive)
      .subscribe(
        {
          next: (result) => {
            if (result) {
              this.isRequesting = false;
            }
          },
          error: (error: string) => this.onError(error)
        });
  }

  primaryContactChanged(event: MatRadioChange) {
    this.isRequesting = true;

    // cannot make an iactive contact the primary contact
    const primarySelectionInactive = this.customer.contacts.find(c => c.contact_ID == event.value).contact_IsActive == false;
    if (primarySelectionInactive) {
      this.primaryContact = this.customer.mainContact == null ? null : this.customer.mainContact.contact_ID.toString();
      this.loadCustomer(this.customer.customer_ID);
      alert($localize`Cannot select an inactive contact as the primary`);
      return;
    }

    this.customerService.setPrimaryContact(this.customer.customer_ID, parseInt(this.primaryContact))
      .subscribe(
        {
          next: (result) => {
            if (result) {
              this.isRequesting = false;
              this.loadCustomer(this.customer.customer_ID);
            }
          },
          error: (error: string) => this.onError(error)
        });
  }

  openContactDialog(contact: Contact): void {
    if (contact == null) {
      contact = {
        customer_ID: this.customer.customer_ID
      } as Contact;
    }

    var cloneContact = Object.assign({}, contact);

    const dialogRef = this.dialog.open(CustomerContactDialog, {
      width: '1250px',
      data: {
        contact: cloneContact,
        contactTypes: this.contactTypes,
        notificationTypes: MessageNnotificationTypes_Enum
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        console.log('The dialog was closed');
        if (result) {
          this.loadCustomer(this.customer.customer_ID);
        }
      }
    });
  }

  deleteCustomer() {
    const proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    this.customerService.deleteCustomer(this.customer.customer_ID)
      .subscribe({
        next: (data) => {
          this.router.navigate(['/customer/']);
        },
        error: (error: string) => this.onError(error)
      });
  }

  activateCustomer() {
    const proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    this.customerService.activateCustomer(this.customer.customer_ID)
      .subscribe({
        next: (data) => {
          this.router.navigate(['/customer/']);
        },
        error: (error: string) => this.onError(error)
      });
  }

  downloadAttachment(note: CustomerNote) {
    this.customerService.getNoteAttachment(note.note_ID)
      .subscribe({
        next: (x) => {
          this.fileService.downloadFile(x, note.note_Attachment);
        },
        error: (error: string) => this.onError(error)
      });
  }
}

@Component({
  selector: 'summary-contact-dialog',
  templateUrl: 'summary.contact.dialog.html',
})
export class CustomerContactDialog extends AbstractBiditComponent {
  constructor(
    deviceDetectorService: DeviceDetectorService,
    private customerService: CustomerService,
    public dialogRef: MatDialogRef<CustomerContactDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ContactDialogData) {
    super(deviceDetectorService);
  }

  saveContact(form: NgForm): void {   

    if (form.valid) {
      this.isRequesting = true;

      this.customerService.saveContact(this.data.contact)
        .subscribe({
          next: (data) => {
            this.dialogRef.close(this.data.contact);
          },
          error: (error: string) => this.onError(error)
        });
    }
  }

  checkVerify(): boolean {
    if (this.data.contact.contact_ReceivesNotifications &&
      (this.data.contact.contact_NotificationDeliveryTypeDesc === MessageNnotificationTypes_Enum.Both ||
        this.data.contact.contact_NotificationDeliveryTypeDesc === MessageNnotificationTypes_Enum.Text) &&
      (this.data.contact.contact_Mobile !== null && this.data.contact.contact_Mobile !== '' && this.data.contact.contact_Mobile !== undefined)) { return true; }
    else { return false; }     
  }

  sendSMSOptIn() {
    this.customerService.sendSMSOptIn(this.data.contact)
      .subscribe({
        next: (data) => {
          this.dialogRef.close(this.data.contact);
        },
        error: (error: string) => this.onError(error)
      });
  }
  

  //validateContactPhone(form: NgForm): boolean {
  //  if ((this.data.contact.contact_Mobile == null || this.data.contact.contact_Mobile == '') &&
  //    (this.data.contact.contact_Phone == null || this.data.contact.contact_Phone == '') &&
  //    (this.data.contact.contact_Email == null || this.data.contact.contact_Email == '')) {
  //    form.controls['contact_Mobile'].setErrors({ 'incorrect': true });
  //    return false;
  //  }
  //}
   

  onNoClick(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'summary-note-dialog',
  templateUrl: 'summary.note.dialog.html',
})
export class CustomerNoteDialog extends AbstractBiditComponent {

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private customerService: CustomerService,
    public dialogRef: MatDialogRef<CustomerNoteDialog>,
    @Inject(MAT_DIALOG_DATA) public data: NoteDialogData) {
    super(deviceDetectorService);
  }

  saveNote(form: NgForm): void {
    if (form.valid) {
      this.isRequesting = true;

      this.customerService.saveNote(this.data.note)
        .subscribe(
          {
            next: (result) => this.dialogRef.close(result),
            error: (error: string) => this.onError(error)
          });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'summary-document-dialog',
  templateUrl: 'summary.document.dialog.html',
})
export class CustomerDocumentDialog extends AbstractBiditComponent {

  fileBytes?: string;
  importFile?: any;

  constructor(
    deviceDetectorService: DeviceDetectorService,
    private customerService: CustomerService,
    public dialogRef: MatDialogRef<CustomerDocumentDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData) {
    super(deviceDetectorService);
  }

  saveDocument(form: NgForm): void {
    if (this.importFile == undefined && this.data.document.note_ID == null) {
      alert($localize`No file provided`);
      return;
    }

    if (form.valid) {
      if (this.importFile == undefined) {
        this.isRequesting = true;

        this.customerService.saveNote(this.data.document)
          .pipe(finalize(() => this.isRequesting = false))
          .subscribe(
            {
              next: (result) => this.dialogRef.close(result),
              error: (error: string) => this.onError(error)
            });
      } else {
        this.saveDocumentAttachment(this.data.document);
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  saveDocumentAttachment(note: CustomerNote) {
    this.isRequesting = true;

    // Set File Information
    note.attachment = {
      fileName: this.importFile.name,
      fileSize: this.importFile.size,
      fileType: this.importFile.type,
      lastModifiedTime: this.importFile.lastModified,
      lastModifiedDate: this.importFile.lastModifiedDate,
    } as FileUpload;

    note.attachment.fileAsBase64 = this.fileBytes;

    this.customerService.saveNote(note)
      .pipe(finalize(() => this.isRequesting = false))
      .subscribe(
        {
          next: (result) => this.dialogRef.close(result),
          error: (error: string) => this.onError(error)
        });
  }
}
